import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Building2, Search, Loader2, FileText,
  MapPin, Calendar, AlertCircle, CheckCircle,
  Download, Clock, RefreshCw, ChevronRight,
  FileSearch, History, XCircle
} from 'lucide-react'
import { useUser } from '../hooks/useUser'
import api from '../services/api'

// ── Types ────────────────────────────────────────────────────

interface CompanyResult {
  piva?: string
  codiceFiscale?: string
  denominazione?: string
  sedeLegale?: { indirizzo?: string; comune?: string; provincia?: string; cap?: string }
  stato?: string
  dataCostituzione?: string
  codiceAteco?: string
  descrizioneAteco?: string
  formaGiuridica?: string
  capitaleSociale?: number
  numeroAddetti?: string
}

type DocType = 'visura_camerale' | 'visura_storica' | 'report_azienda' | 'bilancio' | 'titolare_effettivo'

interface DocumentRecord {
  id: string
  company_piva: string
  company_cf: string
  company_name: string
  document_type: DocType
  status: 'pending' | 'processing' | 'completed' | 'failed'
  error_message?: string
  display_name: string
  file_size_bytes?: number
  created_at: string
  completed_at?: string
}

const DOC_TYPE_OPTIONS: { value: DocType; label: string; available: boolean }[] = [
  { value: 'visura_camerale', label: 'Scheda Azienda Registro Imprese', available: true },
  { value: 'visura_storica', label: 'Visura Camerale Storica', available: false },
  { value: 'report_azienda', label: 'Report Azienda', available: false },
  { value: 'bilancio', label: 'Bilancio Riclassificato', available: false },
  { value: 'titolare_effettivo', label: 'Titolare Effettivo', available: false },
]

const STATUS_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  pending: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400', label: 'In attesa' },
  processing: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', label: 'In elaborazione' },
  completed: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', label: 'Completato' },
  failed: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', label: 'Errore' },
}

// ── Component ────────────────────────────────────────────────

export default function BusinessIntelligence() {
  const { user } = useUser()
  const queryClient = useQueryClient()

  const [searchQuery, setSearchQuery] = useState('')
  const [searchType, setSearchType] = useState<'name' | 'piva' | 'cf'>('name')
  const [searchResults, setSearchResults] = useState<CompanyResult[]>([])
  const [selectedCompany, setSelectedCompany] = useState<CompanyResult | null>(null)
  const [selectedDocType, setSelectedDocType] = useState<DocType>('visura_camerale')
  const [tab, setTab] = useState<'search' | 'history'>('search')

  const hasCompanyLookup = user?.license?.company_lookup || false
  const hasCameralRecords = user?.license?.cameral_records || false

  // ── Search mutation ──
  const searchMutation = useMutation({
    mutationFn: async ({ query, type }: { query: string; type: string }) => {
      const r = await api.post('/business/company-lookup', { query, searchType: type })
      return r.data
    },
    onSuccess: (data) => {
      if (data.results) {
        setSearchResults(data.results)
        setSelectedCompany(null)
      }
    },
  })

  // ── Document request mutation ──
  const requestMutation = useMutation({
    mutationFn: async (params: { piva: string; codice_fiscale: string; company_name: string; document_type: DocType }) => {
      const r = await api.post('/business/document-request', params)
      return r.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-documents'] })
    },
  })

  // ── Document history query ──
  const historyQuery = useQuery({
    queryKey: ['company-documents'],
    queryFn: async () => {
      const r = await api.get('/business/documents?limit=50')
      return r.data as { documents: DocumentRecord[]; total: number }
    },
    refetchInterval: 30000,
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (!searchQuery.trim()) return
    searchMutation.mutate({ query: searchQuery.trim(), type: searchType })
  }

  const handleRequest = () => {
    if (!selectedCompany) return
    requestMutation.mutate({
      piva: selectedCompany.piva || '',
      codice_fiscale: selectedCompany.codiceFiscale || '',
      company_name: selectedCompany.denominazione || '',
      document_type: selectedDocType,
    })
  }

  const handleDownload = async (doc: DocumentRecord) => {
    try {
      const r = await api.get(`/business/documents/${doc.id}/download`, { responseType: 'blob' })
      const url = URL.createObjectURL(r.data)
      const a = document.createElement('a')
      a.href = url
      a.download = `${doc.display_name.replace(/[^a-zA-Z0-9_\-.àèéìòù ]/g, '_')}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      // handled by error boundary
    }
  }

  // ── Gated state ──
  if (!hasCompanyLookup && !hasCameralRecords) {
    return (
      <div className="space-y-6">
        <div className="card">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              Funzionalità non disponibile
            </h3>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mb-4">
              Il tuo piano attuale non include l'accesso al Registro Imprese.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              Passa a un piano superiore per sbloccare:
            </p>
            <ul className="text-sm text-left text-gray-600 dark:text-gray-400 mt-2 space-y-1">
              <li>• Ricerca aziende per P.IVA, Codice Fiscale o Ragione Sociale</li>
              <li>• Visura camerale ordinaria e archivio richieste</li>
              <li>• Archivio documenti con storico richieste</li>
            </ul>
          </div>
        </div>
      </div>
    )
  }

  // ── Main render ──
  return (
    <div className="space-y-6 pt-1">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
            Registro Imprese
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
            Ricerca azienda e scheda dati dal Registro Imprese con archivio richieste
          </p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-slate-700">
        <button
          onClick={() => setTab('search')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            tab === 'search'
              ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          <FileSearch className="w-4 h-4" />
          Nuova richiesta
        </button>
        <button
          onClick={() => setTab('history')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            tab === 'history'
              ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          <History className="w-4 h-4" />
          Storico documenti
          {(historyQuery.data?.total ?? 0) > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400">
              {historyQuery.data?.total}
            </span>
          )}
        </button>
      </div>

      {/* ── SEARCH TAB ── */}
      {tab === 'search' && (
        <div className="space-y-5">
          {/* Search form */}
          {hasCompanyLookup && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  Ricerca Azienda
                </h3>
              </div>

              <form onSubmit={handleSearch} className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <select
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value as 'name' | 'piva' | 'cf')}
                    className="border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                  >
                    <option value="name">Ragione Sociale</option>
                    <option value="piva">Partita IVA</option>
                    <option value="cf">Codice Fiscale</option>
                  </select>

                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={
                      searchType === 'name'
                        ? 'es. Esempio S.r.l.'
                        : searchType === 'piva'
                        ? 'es. 01234567890'
                        : 'es. 01234567890'
                    }
                    className="flex-1 border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                  />

                  <button
                    type="submit"
                    disabled={searchMutation.isPending || !searchQuery.trim()}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    {searchMutation.isPending ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Ricerca...</>
                    ) : (
                      <><Search className="w-4 h-4" /> Cerca</>
                    )}
                  </button>
                </div>

                {searchMutation.isError && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-700 dark:text-red-300">
                      Errore durante la ricerca. Verifica i dati inseriti e riprova.
                    </div>
                  </div>
                )}
              </form>
            </div>
          )}

          {/* Search results */}
          {searchResults.length > 0 && !selectedCompany && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Risultati ({searchResults.length})
                </h3>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-slate-700">
                {searchResults.map((c, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedCompany(c)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {c.denominazione || 'N/D'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {c.piva && `P.IVA ${c.piva}`}
                        {c.piva && c.sedeLegale?.comune && ' · '}
                        {c.sedeLegale?.comune && `${c.sedeLegale.comune} (${c.sedeLegale.provincia})`}
                        {c.formaGiuridica && ` · ${c.formaGiuridica}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      {c.stato && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          c.stato.toLowerCase().includes('attiv')
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-400'
                        }`}>
                          {c.stato}
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search empty state */}
          {searchMutation.isSuccess && searchResults.length === 0 && (
            <div className="card">
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <XCircle className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Nessuna azienda trovata. Verifica i dati e riprova.
                </p>
              </div>
            </div>
          )}

          {/* Selected company + document request */}
          {selectedCompany && (
            <div className="card">
              <div className="card-header flex items-center justify-between">
                <h3 className="card-title flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  {selectedCompany.denominazione || 'Azienda selezionata'}
                </h3>
                <button
                  onClick={() => { setSelectedCompany(null); setSearchResults([]); requestMutation.reset() }}
                  className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Nuova ricerca
                </button>
              </div>

              {/* Company summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {selectedCompany.piva && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Partita IVA</p>
                    <p className="text-sm font-mono text-gray-900 dark:text-white">{selectedCompany.piva}</p>
                  </div>
                )}
                {selectedCompany.codiceFiscale && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Codice Fiscale</p>
                    <p className="text-sm font-mono text-gray-900 dark:text-white">{selectedCompany.codiceFiscale}</p>
                  </div>
                )}
                {selectedCompany.formaGiuridica && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Forma Giuridica</p>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.formaGiuridica}</p>
                  </div>
                )}
                {selectedCompany.sedeLegale?.comune && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><MapPin className="w-3 h-3" /> Sede</p>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedCompany.sedeLegale.comune} ({selectedCompany.sedeLegale.provincia})
                    </p>
                  </div>
                )}
                {selectedCompany.stato && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Stato</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {selectedCompany.stato.toLowerCase().includes('attiv')
                        ? <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                        : <AlertCircle className="w-3.5 h-3.5 text-amber-500" />}
                      <span className="text-sm text-gray-900 dark:text-white">{selectedCompany.stato}</span>
                    </div>
                  </div>
                )}
                {selectedCompany.codiceAteco && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">ATECO</p>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.codiceAteco}</p>
                  </div>
                )}
                {selectedCompany.dataCostituzione && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><Calendar className="w-3 h-3" /> Costituzione</p>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {new Date(selectedCompany.dataCostituzione).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                )}
                {selectedCompany.capitaleSociale && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Capitale Sociale</p>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(selectedCompany.capitaleSociale)}
                    </p>
                  </div>
                )}
              </div>

              {/* Document type selection + request */}
              {hasCameralRecords && (
                <div className="border-t border-gray-200 dark:border-slate-700 pt-5">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Richiedi documento
                  </h4>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <select
                      value={selectedDocType}
                      onChange={(e) => setSelectedDocType(e.target.value as DocType)}
                      className="border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                    >
                      {DOC_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value} disabled={!opt.available}>
                          {opt.label}{!opt.available ? ' (prossimamente)' : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleRequest}
                      disabled={requestMutation.isPending || !DOC_TYPE_OPTIONS.find(o => o.value === selectedDocType)?.available}
                      className="btn btn-primary flex items-center gap-2"
                    >
                      {requestMutation.isPending ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Richiesta in corso...</>
                      ) : (
                        <><FileText className="w-4 h-4" /> Richiedi</>
                      )}
                    </button>
                  </div>

                  {/* Request result */}
                  {requestMutation.isSuccess && (
                    <div className="mt-3 flex items-start gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-green-700 dark:text-green-300">
                        {requestMutation.data?.deduplicated
                          ? 'Documento già disponibile (richiesto di recente).'
                          : 'Documento generato con successo.'}
                        {' '}Vai allo <button onClick={() => setTab('history')} className="underline font-medium">storico</button> per visualizzarlo.
                      </div>
                    </div>
                  )}

                  {requestMutation.isError && (
                    <div className="mt-3 flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-red-700 dark:text-red-300">
                        Errore nella richiesta del documento. Riprova più tardi.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── HISTORY TAB ── */}
      {tab === 'history' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Storico richieste
            </h3>
            <button
              onClick={() => queryClient.invalidateQueries({ queryKey: ['company-documents'] })}
              className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center gap-1"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Aggiorna
            </button>
          </div>

          {historyQuery.isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          )}

          {historyQuery.isSuccess && (historyQuery.data?.documents?.length ?? 0) === 0 && (
            <div className="card">
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <FileText className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  Nessun documento richiesto
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                  Cerca un'azienda e richiedi una scheda azienda per iniziare.
                </p>
              </div>
            </div>
          )}

          {historyQuery.isSuccess && (historyQuery.data?.documents?.length ?? 0) > 0 && (
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-slate-700">
                      <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Azienda</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Documento</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Data</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Stato</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-slate-700/50">
                    {historyQuery.data!.documents.map((doc) => {
                      const badge = STATUS_BADGE[doc.status] || STATUS_BADGE.failed
                      return (
                        <tr key={doc.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/30">
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900 dark:text-white truncate max-w-[200px]">
                              {doc.company_name || doc.company_piva || '-'}
                            </p>
                            {doc.company_piva && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">
                                P.IVA {doc.company_piva}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                            {DOC_TYPE_OPTIONS.find(o => o.value === doc.document_type)?.label || doc.document_type}
                          </td>
                          <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            <span className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5" />
                              {new Date(doc.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                              {badge.label}
                            </span>
                            {doc.error_message && (
                              <p className="text-xs text-red-500 mt-0.5 max-w-[180px] truncate" title={doc.error_message}>
                                {doc.error_message}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {doc.status === 'completed' && (
                              <button
                                onClick={() => handleDownload(doc)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
                              >
                                <Download className="w-3.5 h-3.5" />
                                Scarica
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
